/**
 * useQuery hook for fetching proposals, budget and merge-tag data for a story.
 *
 * Replaces `storyViewEffects.getProposalsInStory` (compose-app). Calls the
 * same REST endpoint: `POST /api/stories/v1/stories/getProposalsInStory`.
 *
 * Auth: `transport` (cookie/session — REST endpoint, not GraphQL, §1).
 *
 * Pass `null` to disable when `canAddProposalContent` flag is off or during
 * route transitions before `entityId > 0` (§9).
 */

import { useQuery } from '@tanstack/react-query';
import { transport } from '@opengov/app-core/transport';
import { IAllProposalDetails, IBudget, IMergeTags } from '@opengov/stories-shared/types';

import { useToast } from '../../Toast/context/useToast.context';
import { storyViewQueryKeys, ProposalsKeyParams } from './storyViewQueryKeys';

export type UseProposalsInStoryParams = ProposalsKeyParams | null;

export type ProposalsInStoryData = {
  proposalFieldsDetails: IAllProposalDetails;
  storyBudget: IBudget;
  mergeTags: IMergeTags;
};

export function useProposalsInStory(
  params: UseProposalsInStoryParams,
  errorMessage: string
) {
  const { toggleToast } = useToast();

  return useQuery<ProposalsInStoryData, Error>({
    queryKey: params
      ? storyViewQueryKeys.proposals(params)
      : ([...storyViewQueryKeys.all, 'proposals', 'disabled'] as const),
    queryFn: async () => {
      if (!params) throw new Error('Disabled');
      const { storyId, entityId } = params;

      return transport
        .url(`/api/stories/v1/stories/getProposalsInStory`)
        .json({ story_id: storyId, entity_id: entityId })
        .post()
        .json()
        .then(
          ({
            proposalFieldsDetails,
            storyBudget,
            mergeTags,
          }: ProposalsInStoryData) => ({
            proposalFieldsDetails,
            storyBudget,
            mergeTags,
          })
        );
    },
    enabled: params !== null,
    onError: () => {
      toggleToast({ title: errorMessage, severity: 'error' });
    },
  });
}
